import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { ToastContainer } from "../components/Toast/Toast";
import {
  ToastConfig,
  ToastContextData,
  ToastData,
  ToastProps,
} from "../interfaces/all";
import { CacheManager } from "../utils/cache-manager";
import { ErrorHandler } from "../utils/error-handler";

const MAX_TOASTS = 5;
const TOAST_CONFIG_CACHE_KEY = "toast_config";
const TOAST_CONFIG_CACHE_OPTIONS = { expirationDays: 30 };

const defaultToastConfig: ToastConfig = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: "dark",
};

const ToastContext = createContext<ToastContextData>({} as ToastContextData);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [config, setConfig] = useState<ToastConfig>(() => {
    const cachedConfig = CacheManager.get<ToastConfig>(
      TOAST_CONFIG_CACHE_KEY,
      TOAST_CONFIG_CACHE_OPTIONS,
    );
    return cachedConfig || defaultToastConfig;
  });
  const timeoutRefs = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  const removeToast = useCallback((id: string) => {
    setToasts((state) => state.filter((toast) => toast.id !== id));
    // Limpa o timeout se existir
    const timeoutId = timeoutRefs.current.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutRefs.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (props: Omit<ToastProps, "onClose">) => {
      const id = uuidv4();
      const toast: ToastData = {
        id,
        type: props.type,
        message: props.message,
      };

      setToasts((state) => {
        const newToasts = [...state, toast];
        return newToasts.length > MAX_TOASTS
          ? newToasts.slice(-MAX_TOASTS)
          : newToasts;
      });

      // Remove o toast após o tempo especificado
      const timeoutId = setTimeout(
        () => {
          removeToast(id);
        },
        (props.duration || config.autoClose) + 300,
      );

      // Armazena a referência do timeout
      timeoutRefs.current.set(id, timeoutId);

      return () => removeToast(id);
    },
    [removeToast, config.autoClose],
  );

  const showError = useCallback(
    (error: unknown, options?: { duration?: number }) => {
      const duration = options?.duration || 7000; // Erros ficam visíveis por mais tempo

      if (typeof error === "string") {
        showToast({
          type: "error",
          message: error,
          duration,
        });
        return;
      }

      if (error && typeof error === "object" && "userMessage" in error) {
        showToast({
          type: "error",
          message: (error as { userMessage: string }).userMessage,
          duration,
        });
        return;
      }

      // Se não for uma string ou objeto com userMessage, processa o erro
      const errorResponse = ErrorHandler.handleApiError(error);
      showToast({
        type: "error",
        message: errorResponse.userMessage,
        duration,
      });
    },
    [showToast],
  );

  const showSuccess = useCallback(
    (message: string) => {
      showToast({
        type: "success",
        message,
        duration: config.autoClose,
      });
    },
    [showToast, config.autoClose],
  );

  const updateConfig = useCallback((newConfig: Partial<ToastConfig>) => {
    setConfig((prev) => {
      const updated = { ...prev, ...newConfig };
      CacheManager.set(
        TOAST_CONFIG_CACHE_KEY,
        updated,
        TOAST_CONFIG_CACHE_OPTIONS,
      );
      return updated;
    });
  }, []);

  // Limpa todos os toasts e timeouts quando o componente é desmontado
  useEffect(() => {
    return () => {
      setToasts([]);
      // Limpa todos os timeouts pendentes
      timeoutRefs.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      timeoutRefs.current.clear();
    };
  }, []);

  return (
    <ToastContext.Provider
      value={{ showToast, showError, showSuccess, updateConfig, config }}
    >
      {children}
      <ToastContainer
        toasts={toasts.map((toast, index) => ({
          ...toast,
          onClose: () => removeToast(toast.id),
          duration: config.autoClose,
        }))}
        onClose={(index) => removeToast(toasts[index]?.id || "")}
        position={config.position}
        theme={config.theme}
      />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }

  return context;
};
